import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import { defaultFAQ, defaultFAQValidation } from '../constants/editFAQ';
import { FAQCast, getFAQParams } from '../helpers/editFAQ';
import { IFAQ, IFAQValidation, IResponseFAQ } from '../types/editFAQ';
import client from 'src/services/client';
import { LoadingStatus } from 'src/types/main';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';
import { emptyNameValidation } from '~/utils/validation';

class EditFAQStore {
  constructor() {
    makeObservable(this);
  }

  private getFAQAbortController = getAbortController();

  @observable public loading: LoadingStatus = LoadingStatus.Init;
  @observable public isEdit: boolean = false;

  @observable public FAQ: IFAQ = defaultFAQ;
  @observable public validation: IFAQValidation = cloneDeep(defaultFAQValidation);

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Refreshing, LoadingStatus.Loading].includes(this.loading);
  }

  @action public init = (id: string | undefined) => {
    this.validation = cloneDeep(defaultFAQValidation);
    this.FAQ = defaultFAQ;
    this.isEdit = false;

    if (id) this.getFAQ(id);
  };

  @action private getFAQ = async (id: string) => {
    this.getFAQAbortController.abort();
    this.getFAQAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const faq: IResponseFAQ = await client.get(`v4/website-info/faq/${id}`, {
        headers: { 'Accept-Language': 'en,ar' },
        signal: this.getFAQAbortController.signal,
      });

      this.setFAQ(faq);

      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'ManageFAQStore changePage', showModal: true });
    }
  };

  @action private setFAQ = (faq?: IResponseFAQ) => {
    this.FAQ = faq ? FAQCast(faq) : defaultFAQ;
  };

  @action changeFAQHandler = (value: Partial<IFAQ>) => {
    this.FAQ = { ...this.FAQ, ...value };

    this.isEdit = true;
  };

  @action public onSubmit = () => {
    if (this.FAQ.id) return this.updateFAQ();

    return this.createFAQ();
  };

  @action private updateFAQ = async () => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.put(`v4/website-info/faq/${this.FAQ.id}`, getFAQParams(this.FAQ));

      this.isEdit = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'ManageFAQStore updateFAQ', showModal: true });
    }
  };

  @action private createFAQ = async () => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.post(`v4/website-info/faq`, getFAQParams(this.FAQ));

      this.isEdit = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;

      if (!this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'ManageFAQStore createFAQ', showModal: true });
    }
  };

  @action private isErrorResponseHandled(error: any) {
    const errorsBag = error?.response?.data?.errors || {};

    this.validation = cloneDeep(defaultFAQValidation);

    if ('description.en.question' in errorsBag) {
      this.validation.question.enValid = false;
      this.validation.question.enErrorText = errorsBag['description.en.question'][0];
      this.validation.isValid = false;
    }
    if ('description.ar.question' in errorsBag) {
      this.validation.question.arValid = false;
      this.validation.question.arErrorText = errorsBag['description.ar.question'][0];
      this.validation.isValid = false;
    }

    if ('description.en.answer' in errorsBag) {
      this.validation.answer.enValid = false;
      this.validation.answer.enErrorText = errorsBag['description.en.answer'][0];
      this.validation.isValid = false;
    }
    if ('description.ar.answer' in errorsBag) {
      this.validation.answer.arValid = false;
      this.validation.answer.arErrorText = errorsBag['description.ar.answer'][0];
      this.validation.isValid = false;
    }

    return this.validation.isValid;
  }

  @action public updateValidation(validation: Partial<IFAQValidation>) {
    this.validation = { ...this.validation, ...validation };
  }

  @action public setValidation = (): boolean => {
    this.validation = cloneDeep(defaultFAQValidation);

    // question validation
    [this.validation.question, this.validation.isValid] = emptyNameValidation(this.FAQ.question, this.validation.question, this.validation.isValid);

    // answer validation
    [this.validation.answer, this.validation.isValid] = emptyNameValidation(this.FAQ.answer, this.validation.answer, this.validation.isValid);

    return this.validation.isValid;
  };
}

const editFAQStore = new EditFAQStore();

export default editFAQStore;
